import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import uuid from 'uuid/v4';

import { COMPONENT_TEMPLATE, EDIT_STATUS_NEW } from 'app/App/const';
import { switchApplicationMode } from 'app/App/actions';
import editStatusSelector from 'app/App/selectors/editStatus';
import contractMapSelector from 'app/App/selectors/contractMap';
import {
  getExistingComponentFailure,
  openComponentForEditing
} from 'app/CanvasComponent/actions';
import {
  addComponentToPageThunk,
  addMasterComponentToPageThunk,
  checkStatusAndDispatchActionsThunk
} from 'app/ComponentSearch/thunks';
import { COUNTRY_CODE_LOCATION } from 'app/ContentContext/const';
import { fetchExperiment } from 'app/Experiments/thunks';
import { isExperiment } from 'app/Experiments/utils';
import { notifyAlert, notifySuccess, notifyFailure } from 'app/Notifications/actions';
import {
  changePageProperty,
  createUnsavedPage,
  duplicatePage,
  removeActivePage,
  removeComponentFromPage,
  replaceComponentOnPage,
  savePageFailure,
  savePageSuccess
} from 'app/Page/actions';
import pagePayloadSelector from 'app/Page/selectors/pagePayload';
import { PAGE_TEMPLATES, PAGE_TYPES } from 'app/RenderingEnvironment/const';
import { visitFolder, visitLogout, visitPage } from 'app/Router/actions';
import cwdSiteRootSelector from 'app/Tree/selectors/cwdSiteRoot';
import { getFolderThunk } from 'app/Tree/thunks';
import lanIdSelector from 'app/UserInfo/selectors/lanId';
import { getMostRecentComponentsThunk } from 'app/UserInfo/thunks';
import { BOUND_PROPLOCATION_PATH, DEFAULT_BOUND_VALUE, CONTENT_TYPES } from 'lib/const';
import { ptToUtcDateString } from 'lib/date';
import { isBlank } from 'lib/string';
import personalizedHomepage from 'lib/templates/personalizedHomepage';
import { PUBLISHED } from 'lib/versions';
import {
  findLinkURLsToValidate,
  getInvalidLinkMessage,
  identifyInvalidLinks,
  isContentPoolPlacement
} from 'lib/validations';
import {
  UI_ADD_NEW_COMPONENT_TO_PAGE,
  UI_EDIT_COMPONENT_ON_PAGE,
  createNewComponentObject,
  createNewPageObject,
  duplicateComponent
} from 'utils/factory';
import { toggleAddPage, toggleCreateMasterComponent } from './actions';

// HELPER FUNCS FOR THUNKS
/**
 * helper func that traverses through nested subcomponents and transforms any found content pool placements
 * transformation does two things: 1. removes any child components, 2. sets a default 'subcomponentBound' value if not defined
 * @param {object} params - function params
 * @param {Array} params.components - list of child components
 * @param {object} params.parentComponent - the parent component for the current list of `components` (above param)
 * @returns {object} page data with transformed content pool placements
 */
export const getTransformedContentPoolPlacements = ({
  components = [],
  parentComponent = undefined
}) => {
  const transformedChildren = components.map(comp => {
    if (isContentPoolPlacement({ component: comp })) {
      const transformedContentPoolPlacement = { ...comp };
      if (!get(comp, BOUND_PROPLOCATION_PATH, undefined)) {
        set(transformedContentPoolPlacement, BOUND_PROPLOCATION_PATH, DEFAULT_BOUND_VALUE);
      }
      if (get(comp, comp.ChildLocation).length) {
        set(transformedContentPoolPlacement, comp.ChildLocation, []);
      }
      return transformedContentPoolPlacement;
    } else if (comp.ChildLocation) {
      const childComponents = get(comp, comp.ChildLocation);
      return getTransformedContentPoolPlacements({
        components: childComponents,
        parentComponent: comp
      });
    }
    return comp;
  });
  if (parentComponent) {
    const newParentComponent = { ...parentComponent };
    set(newParentComponent, parentComponent.ChildLocation, transformedChildren);
    return newParentComponent;
  }
  return transformedChildren;
};

/**
 * helper func that returns the page data with transformed content pool placements
 * @param {object} params - function params
 * @param {object} params.page - page data
 * @returns {object} page data with transformed content pool placements
 */
export const transformContentPoolPlacements = ({ page = {} }) => {
  const {
    Payload: { Components: components = [] }
  } = page;
  const pageWithTransformedContentPoolPlacements = { ...page };
  const transformedContentPoolPlacements = getTransformedContentPoolPlacements({ components });
  pageWithTransformedContentPoolPlacements.Payload.Components = transformedContentPoolPlacements;
  return pageWithTransformedContentPoolPlacements;
};

// THUNKS
/**
 * thunk to save a page
 * @returns {object} returns object containing id, version and parentFolderId of the page after
 * save or undefined(in case of save failure)
 */
export const savePageThunk = () => async (dispatch, getState, { api }) => {
  const state = getState();
  const editStatus = editStatusSelector(state);
  let pagePayload = pagePayloadSelector(state);

  const site = cwdSiteRootSelector(state);

  try {
    dispatch(notifyAlert(`Saving page: ${pagePayload.PageName}`));

    // validate any link urls
    const { Payload: { Components = [] } = {} } = pagePayload;
    const allLinksToValidate = Components.reduce((acc, curr) => {
      const countryList = get(curr, `Payload.MetadataFields.${COUNTRY_CODE_LOCATION}`, []);
      const linksToValidate = findLinkURLsToValidate({ component: curr, countryList, site });
      return [...acc, ...linksToValidate];
    }, []);
    const validatedLinks =
      allLinksToValidate.length && (await api.misc.validateLinkURLs(allLinksToValidate));
    const invalidLinks = validatedLinks && identifyInvalidLinks(validatedLinks);

    // make sure content pool placements have default 'bound' values and no child components before saving the page
    pagePayload = transformContentPoolPlacements({ page: pagePayload });

    const response =
      editStatus === EDIT_STATUS_NEW
        ? await api.page.addPage(pagePayload)
        : await api.page.savePage(pagePayload);
    dispatch(savePageSuccess());
    dispatch(getMostRecentComponentsThunk());

    if (invalidLinks.length) {
      const failureMessage = getInvalidLinkMessage(invalidLinks);
      dispatch(notifyFailure(failureMessage));
    }
    dispatch(notifySuccess(`Page saved: ${pagePayload.PageName}`));
    return {
      id: response.Id,
      version: response.Version,
      parentFolderId: pagePayload.ParentFolderId
    };
  } catch (error) {
    if (error.response && error.response.status === 409) {
      dispatch(savePageFailure(pagePayload, error));
      dispatch(notifyFailure(undefined));
    } else {
      dispatch(notifyFailure(`Save page failed: ${error}`));
    }
    return undefined;
  }
};

/**
 * thunk to save a page and visit to the new version
 * @returns {object} returns object containing id, version and parentFolderId of the page after
 * save or undefined(in case of save failure)
 */
export const saveAndVisitPageThunk = () => async dispatch => {
  const response = await dispatch(savePageThunk());
  if (response) {
    dispatch(visitPage(response.id, response.version));
    dispatch(getFolderThunk(response.parentFolderId));
  }
  return response;
};

/**
 * changes a page property
 * @param {string} value - value for an edited page property
 * @param {string} field - corresponding value for page property
 * @returns {undefined}
 */
export const changePagePropertyDetail = (value, field) => dispatch => {
  dispatch(changePageProperty(value, field));
};

/**
 * adds base component pool and page information for a PHP
 * todo: this will need to be updated when we have more templates
 * @returns {undefined}
 */
export const addTemplateProperties = () => async (dispatch, getState) => {
  const errors = [];
  personalizedHomepage.newComponents.forEach(item => {
    const { componentVariation, templateName, name } = item;

    try {
      const state = getState();
      const contractMap = contractMapSelector(state);
      const lanId = lanIdSelector(state);

      const component = createNewComponentObject({
        componentVariation,
        canvasContract: contractMap[componentVariation],
        componentName: `${name}-${uuid()}`,
        renderingEnv: {
          pageTemplateName: PAGE_TEMPLATES.PHP_TEMPLATE,
          pageType: PAGE_TYPES.PERSONALIZED_HOMEPAGE
        },
        templateName: templateName || 'null',
        userLanId: lanId
      });
      component.uiAction = UI_ADD_NEW_COMPONENT_TO_PAGE;
      dispatch(addComponentToPageThunk({ component, openForEditing: false }));
    } catch (error) {
      errors.push(error);
    }
  });

  await Promise.all(
    personalizedHomepage.existingComponents.map(async id => {
      try {
        await dispatch(
          addMasterComponentToPageThunk({
            id,
            openForEditing: false
          })
        );
      } catch (error) {
        errors.push(error);
      }
    })
  );

  errors.forEach(item => dispatch(notifyFailure(item)));

  dispatch(
    changePageProperty(
      personalizedHomepage.metaData.SeoSettings,
      'Payload.MetadataFields.SeoSettings'
    )
  );
  dispatch(
    changePageProperty(
      personalizedHomepage.metaData.CategoryId,
      'Payload.MetadataFields.CategoryId'
    )
  );
  dispatch(
    changePageProperty(personalizedHomepage.metaData.Route, 'Payload.MetadataFields.Route')
  );
};

/**
 * add unsaved page
 * @param {object} payload - new page details
 * @returns {undefined}
 */
export const createNewUnsavedPage = payload => async (dispatch, getState, { api }) => {
  if (payload.isFlashEvent) {
    try {
      dispatch(notifyAlert(`Creating '${payload.pageName}'...`));
      const flashEventPageRunAsOf = ptToUtcDateString(
        `${payload.pageDate} ${payload.pageTime}`
      );
      const res = await api.workflow.createFlashEventPage({
        ...payload,
        flashEventPageRunAsOf
      });

      const { landingPage } = res;
      if (!landingPage) {
        throw new Error('Response did not include `landingPage`');
      }

      dispatch(visitPage(landingPage, PUBLISHED));
      dispatch(notifySuccess(`Successfully created '${payload.pageName}'!`));
    } catch (err) {
      dispatch(notifyFailure(err.toString()));
    }
  } else {
    const state = getState();
    const cwdSiteRoot = cwdSiteRootSelector(state);
    dispatch(createUnsavedPage(createNewPageObject({ cwdSiteRoot, ...payload })));
  }

  // todo: update this when we have more than one template
  if (
    payload.renderingEnv &&
    payload.renderingEnv.pageTemplateName === 'Personalized Homepage Template'
  ) {
    dispatch(addTemplateProperties());
  }
};

/**
 * removes the selected component from an existing page and notifies the user
 * @param {string} componentName - component's user given name
 * @param {string} id - uuid of component
 * @returns {undefined}
 */
export const removeAddedComponentFromPage = (componentName, id) => dispatch => {
  dispatch(removeComponentFromPage(id));
  dispatch(notifySuccess(`Component ${componentName} removed from page`));
};

/**
 * switches editor pane for to editor form for the selected component
 * keeping page on the canvas.
 * @param {string} id - new page uuid
 * @returns {Function} thunk
 */
export const editComponentOnPageThunk = ({ index }) => (dispatch, getState) => {
  try {
    const pagePayload = pagePayloadSelector(getState());
    const component = pagePayload.Payload.Components[index];
    if (!component) {
      throw new Error(`No component is not found at index ${index} on the page.`);
    }

    component.uiAction = UI_EDIT_COMPONENT_ON_PAGE;
    dispatch(openComponentForEditing(component));

    if (isExperiment(component) && !isBlank(component.Payload.experimentName)) {
      dispatch(fetchExperiment(component.Payload.experimentName));
    }

    // switch to component mode
    dispatch(switchApplicationMode(COMPONENT_TEMPLATE));
  } catch (error) {
    dispatch(getExistingComponentFailure(`index: ${index}`, error));
    dispatch(notifyFailure(error));
  }
};

/**
 * add new component to page
 * @param {object} params - function params
 * @param {string} params.componentVariation - component variation of the selected component
 * @param {object} params.renderingEnv - rendering env object of the selected component
 * @returns {Function} thunk
 */
export const addNewComponentToPageThunk = ({ componentVariation, renderingEnv }) => (
  dispatch,
  getState
) => {
  try {
    const state = getState();
    const contractMap = contractMapSelector(state);
    const cwdSiteRoot = cwdSiteRootSelector(state);
    const lanId = lanIdSelector(state);

    const component = createNewComponentObject({
      componentVariation,
      cwdSiteRoot,
      renderingEnv,
      canvasContract: contractMap[componentVariation],
      userLanId: lanId
    });
    component.uiAction = UI_ADD_NEW_COMPONENT_TO_PAGE;
    dispatch(addComponentToPageThunk({ component }));
    dispatch(
      notifySuccess(`A new ${componentVariation} component has been added to the page.`)
    );
  } catch (error) {
    dispatch(notifyFailure(error));
  }
};

/**
 * replace a component with a copy on the page
 *
 * @param {object} params - function params
 * @param {object} params.component - componetn data
 * @param {number} params.index - page index
 * @returns {Function} thunk
 */
export const replaceComponentWithACopyThunk = ({ index, component }) => (
  dispatch,
  getState
) => {
  try {
    const state = getState();
    const lanId = lanIdSelector(state);

    const duplicatedComponent = duplicateComponent({ component, lanId });
    dispatch(replaceComponentOnPage(index, duplicatedComponent));
    dispatch(notifySuccess('Component has been replaced with a copy.'));
  } catch (error) {
    dispatch(notifyFailure(error));
  }
};

/**
 * attempts to move a page to a new folder and refreshes entire tree.
 * @param {string} currentParentId - id of current parent folder
 * @param {object} selectedContent - selectedContent object containing id, name
 * and type (page or folder) for the content to be moved
 * @param {object} parentFolder - parentFolder object containing id and name of the new parent folder
 * @returns {undefined}
 */
export const moveContentThunk = (currentParentId, selectedContent, parentFolder) => async (
  dispatch,
  getState,
  { api }
) => {
  dispatch(
    notifyAlert(
      `Moving ${selectedContent.type} ${selectedContent.name} to folder ${parentFolder.name}.`
    )
  );
  try {
    if (currentParentId === parentFolder.id) {
      dispatch(
        notifyFailure(
          `${selectedContent.name} is already in ${parentFolder.name} directory. Move Aborted.`
        )
      );
    } else {
      if (selectedContent.type === CONTENT_TYPES.PAGE) {
        await api.page.movePage(selectedContent.id, parentFolder.id);
      } else {
        await api.folder.moveFolder(selectedContent.id, parentFolder.id);
      }

      dispatch(
        notifySuccess(
          `${selectedContent.name} has been moved to folder ${parentFolder.name} successfully.`
        )
      );
      dispatch(visitFolder(currentParentId));
    }
  } catch (error) {
    dispatch(notifyFailure(`Moving ${selectedContent.type} failed: ${error}`));
  }
};

/**
 * attempts to create a duplicate of the selected page in the same folder.
 * @param {string} id - of the selected page to duplicate
 * @param {string} version - version of the page to create the duplicate from
 * @returns {undefined}
 */
export const duplicatePageThunk = (id, version) => async (dispatch, getState, { api }) => {
  dispatch(notifyAlert('Creating a duplicate Page'));
  try {
    const page = await api.page.getPage({ id, version });
    if (page.Id === undefined) {
      dispatch(
        notifyFailure(
          'Duplication failed. Could not retrieve current page data. Please try again.'
        )
      );
      return;
    }
    const oldPageName = page.PageName;
    const newPage = {
      ...cloneDeep(page),
      LastTimestamp: undefined,
      History: [],
      Id: uuid(),
      LiveNow: false,
      Node: [],
      PageName: `Copy of ${page.PageName}`,
      Published: false,
      StartDate: undefined
    };

    const { Tags: { UserTags = [] } = {} } = newPage;
    const looksLikeLanId = /[A-Z0-9]{4}/s;
    const newTags = UserTags.filter(str => !str.match(looksLikeLanId));
    set(newPage, 'Tags.UserTags', newTags);

    dispatch(duplicatePage(newPage));
    dispatch(
      notifySuccess(
        `A duplicate page of ${oldPageName} has been created as ${newPage.PageName}.`
      )
    );
  } catch (error) {
    dispatch(notifyFailure(`Duplication failed: ${error}`));
  }
};

/**
 * navigates to the folder after checking the edit status
 * @param {string} id - of the folder to visit
 * @returns {undefined}
 */
export const checkStatusAndVisitFolderThunk = id => dispatch => {
  dispatch(checkStatusAndDispatchActionsThunk([removeActivePage(), visitFolder(id)]));
};

/**
 * navigates to the logout after checking the edit status
 * @returns {undefined}
 */
export const checkStatusAndVisitLogoutThunk = () => dispatch => {
  dispatch(checkStatusAndDispatchActionsThunk([visitLogout()]));
};

/**
 * creates a duplicate of the page after checking the edit status.
 * @param {string} id - of the selected page to duplicate
 * @param {string} version - version of the page to create the duplicate from
 * @returns {undefined}
 */
export const checkStatusAndDuplicatePageThunk = (id, version) => async dispatch => {
  dispatch(checkStatusAndDispatchActionsThunk([await duplicatePageThunk(id, version)]));
};

/**
 * navigates to the page after checking the edit status
 * @param {string} id - of the page to visit
 * @param {string} version - of the page to visit
 * @returns {undefined}
 */
export const checkStatusAndVisitPageThunk = (id, version) => dispatch => {
  dispatch(checkStatusAndDispatchActionsThunk([removeActivePage(), visitPage(id, version)]));
};

/**
 * removes active page after checking the edit status
 * @returns {undefined}
 */
export const checkStatusAndRemoveActivePageThunk = () => dispatch => {
  dispatch(checkStatusAndDispatchActionsThunk([removeActivePage()]));
};

/**
 * thunk to show add page dialog after checking edit status
 * @returns {undefined}
 */
export const checkStatusAndToggleAddPageThunk = () => dispatch => {
  dispatch(checkStatusAndDispatchActionsThunk([toggleAddPage()]));
};

/**
 * thunk to show add page dialog after checking edit status
 * @returns {undefined}
 */
export const checkStatusAndToggleCreateMasterComponentThunk = () => dispatch => {
  dispatch(checkStatusAndDispatchActionsThunk([toggleCreateMasterComponent()]));
};
